// src/experiments/assign.js
//
// The bucketing decision. Pure functions over (experiment, uid, context), so
// the same inputs give the same arm in every tab, on every reload, and in a
// test. Nothing here reads storage or the network; callers gather the context.

import { bucket, BUCKETS } from './hash';

export const DEFAULT_GROUP = 'site';
export const DEVICES = ['mobile', 'tablet', 'desktop'];
const FIELDS = [
  'key',
  'traffic',
  'variants',
  'paths',
  'products',
  'device',
  'group',
];

// Keys are joined into analytics as `key:variant|key:variant`, so neither may
// contain `:` or `|`, and lowercase keeps GA4 dimension values predictable.
const KEY_RE = /^[a-z0-9_]+$/;

const warned = new Set();
function warnOnce(id, message) {
  if (process.env.NODE_ENV === 'production' || warned.has(id)) return;
  warned.add(id);
  // eslint-disable-next-line no-console
  console.warn(`[experiments] ${message}`);
}

function clamp01(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return 0;
  return Math.min(1, Math.max(0, n));
}

// Variants may be written as plain keys (an even split) or as { key, weight }
// objects. Everything downstream works on the normalised form.
export function variantsOf(experiment) {
  const list = Array.isArray(experiment?.variants) ? experiment.variants : [];
  if (!list.length) return [];
  if (list.every((v) => typeof v === 'string')) {
    const weight = 1 / list.length;
    return list.map((key) => ({ key, weight }));
  }
  return list.map((v) => ({ key: v?.key, weight: Number(v?.weight) }));
}

// An experiment enrols people only while its traffic is above zero. There is
// no separate status: "not started" and "stopped" are both traffic 0, and
// stopped code is deleted in the next deploy.
export function isActive(experiment) {
  return clamp01(experiment?.traffic) > 0;
}

// One active experiment per group. The default group is the first targeted
// product, so two tests on the car-insurance funnel cannot overlap by accident.
export function groupOf(experiment) {
  return experiment.group ?? experiment.products?.[0] ?? DEFAULT_GROUP;
}

function normalisePath(pathname) {
  const path = String(pathname || '/').toLowerCase();
  return path.length > 1 ? path.replace(/\/+$/, '') : path;
}

// Path targets are segment prefixes: '/vehicleQuoteResults' matches itself and
// '/vehicleQuoteResults/anything'; '/car' does not match '/car-insurance-quote'.
export function pathMatches(pathname, pattern) {
  const path = normalisePath(pathname);
  const target = normalisePath(pattern);
  return path === target || path.startsWith(`${target}/`);
}

export function matchesTargeting(experiment, ctx = {}) {
  const { paths, products, device } = experiment;

  if (Array.isArray(paths) && paths.length) {
    if (!paths.some((pattern) => pathMatches(ctx.pathname, pattern))) {
      return false;
    }
  }
  if (Array.isArray(products) && products.length) {
    if (!products.includes(ctx.product)) return false;
  }
  if (device) {
    const devices = Array.isArray(device) ? device : [device];
    if (!devices.includes(ctx.device)) return false;
  }
  return true;
}

// Two hashes with two salts. `gate` decides whether this uid is in the test at
// all and is the only thing `traffic` touches. `pick` decides the arm over the
// full 0–10000 range, so the arm boundaries never move when traffic does.
//
// This is load-bearing. With a single hash and traffic-scaled boundaries — the
// obvious first implementation — ramping 5% → 50% re-buckets about half of the
// people already enrolled (assign.test.js demonstrates it). With split salts
// raising traffic only ever adds people. Lowering traffic evicts the highest
// gate buckets, which is why lowering should in practice only ever go to 0.
export function assign(experiment, uid, ctx = {}) {
  const variants = variantsOf(experiment);
  if (!variants.length) return null;

  // Crawlers always see control: Prerender.io's snapshot is what gets indexed,
  // and it must not flap between arms.
  if (ctx.isBot) return variants[0].key;

  if (!matchesTargeting(experiment, ctx)) return null;

  const traffic = clamp01(experiment.traffic);
  const gate = bucket(`gate:${experiment.key}:${uid}`);
  if (gate >= traffic * BUCKETS) return null;

  const pick = bucket(`var:${experiment.key}:${uid}`);
  let acc = 0;
  for (const variant of variants) {
    acc += clamp01(variant.weight) * BUCKETS;
    if (pick < acc) return variant.key;
  }
  return variants[0].key;
}

// Assigns every experiment at once. Applies QA overrides, remote forces and
// the session freeze, enforces one active experiment per group (first in
// config order wins), and never enrols crawlers. Returns
// { [experimentKey]: variantKey } for enrolled keys only.
//
// `options.remote` is { key: { force, hard } } from the remote document.
// `options.sticky` is { key: arm } — the arms this visitor has already been
// exposed to in this session. Precedence for one experiment:
//   1. a QA override, always;
//   2. a hard remote force moves everyone, mid-session included;
//   3. an arm already seen this session is kept (the "life of a flow session"
//      freeze), whatever traffic or a soft force now say;
//   4. a soft remote force applies to everyone else;
//   5. the hash, while traffic is above 0.
// Forcing to the first (control) arm means "not enrolled": nothing is stamped.
export function assignAll(
  experiments,
  uid,
  ctx = {},
  overrides = {},
  options = {}
) {
  const result = {};
  if (!Array.isArray(experiments)) return result;

  const remote = options.remote || {};
  const sticky = options.sticky || {};
  const takenGroups = new Set();

  for (const experiment of experiments) {
    if (!experiment || typeof experiment.key !== 'string') continue;
    const { key } = experiment;
    const variants = variantsOf(experiment);

    const forced = overrides[key];
    if (forced && variants.some((v) => v.key === forced)) {
      result[key] = forced;
      continue;
    }

    if (ctx.isBot || !variants.length) continue;
    if (!matchesTargeting(experiment, ctx)) continue;

    const control = variants[0].key;
    const entry = remote[key] || {};
    const remoteForce =
      entry.force && variants.some((v) => v.key === entry.force)
        ? entry.force
        : null;
    const stickyArm =
      sticky[key] && variants.some((v) => v.key === sticky[key])
        ? sticky[key]
        : null;

    let arm = null;
    let claimsGroup = false;
    let useHash = false;
    if (remoteForce && entry.hard) {
      arm = remoteForce === control ? null : remoteForce;
      claimsGroup = arm !== null;
    } else if (stickyArm) {
      arm = stickyArm;
      claimsGroup = true;
    } else if (remoteForce) {
      arm = remoteForce === control ? null : remoteForce;
      claimsGroup = arm !== null;
    } else if (isActive(experiment)) {
      claimsGroup = true;
      useHash = true;
    }

    if (!claimsGroup) continue;
    const group = groupOf(experiment);
    if (takenGroups.has(group)) {
      warnOnce(
        `group:${key}`,
        `"${key}" is not assigning: group "${group}" already has an active experiment earlier in the config.`
      );
      continue;
    }
    takenGroups.add(group);

    if (useHash) arm = assign(experiment, uid, ctx);
    if (arm !== null) result[key] = arm;
  }
  return result;
}

// Config validation. experiments.config.test.js runs it over the registry so a
// bad definition fails the test run before it can ship; the debug overlay
// shows the same messages. Unknown fields are rejected on purpose: the shape
// stays small, and a typo like `path` cannot silently target nothing.
export function validateExperiments(experiments) {
  const errors = [];
  if (!Array.isArray(experiments)) return ['experiments must be an array'];

  const keys = new Set();
  const activeGroups = new Map();

  experiments.forEach((exp, index) => {
    const label = exp?.key ? `"${exp.key}"` : `experiment #${index}`;
    if (!exp || typeof exp !== 'object') {
      errors.push(`${label}: not an object`);
      return;
    }

    for (const field of Object.keys(exp)) {
      if (!FIELDS.includes(field)) {
        errors.push(`${label}: unknown field "${field}"`);
      }
    }

    if (typeof exp.key !== 'string' || !KEY_RE.test(exp.key)) {
      errors.push(`${label}: key must match ${KEY_RE}`);
    } else if (keys.has(exp.key)) {
      errors.push(`${label}: duplicate key`);
    } else {
      keys.add(exp.key);
    }

    const traffic = Number(exp.traffic);
    if (!Number.isFinite(traffic) || traffic < 0 || traffic > 1) {
      errors.push(`${label}: traffic must be a number in [0, 1]`);
    }

    const list = Array.isArray(exp.variants) ? exp.variants : [];
    if (!list.length) {
      errors.push(
        `${label}: needs at least one variant (the first is control)`
      );
    } else if (
      !list.every((v) => typeof v === 'string') &&
      !list.every((v) => v && typeof v === 'object')
    ) {
      errors.push(
        `${label}: variants must be all keys or all { key, weight } objects`
      );
    } else {
      const weighted = typeof list[0] === 'object';
      const seen = new Set();
      let sum = 0;
      variantsOf(exp).forEach(({ key, weight }) => {
        if (typeof key !== 'string' || !KEY_RE.test(key)) {
          errors.push(`${label}: variant keys must match ${KEY_RE}`);
        } else if (seen.has(key)) {
          errors.push(`${label}: duplicate variant "${key}"`);
        } else {
          seen.add(key);
        }
        if (!Number.isFinite(weight) || weight < 0 || weight > 1) {
          errors.push(`${label}: variant weights must be numbers in [0, 1]`);
        } else {
          sum += weight;
        }
      });
      if (weighted && Math.abs(sum - 1) > 1e-6) {
        errors.push(`${label}: variant weights must sum to 1 (got ${sum})`);
      }
    }

    if (isActive(exp)) {
      const group = groupOf(exp);
      if (activeGroups.has(group)) {
        errors.push(
          `${label}: group "${group}" already has active experiment "${activeGroups.get(group)}"`
        );
      } else {
        activeGroups.set(group, exp.key);
      }
    }

    for (const field of ['paths', 'products']) {
      const value = exp[field];
      if (
        value !== undefined &&
        (!Array.isArray(value) || !value.every((s) => typeof s === 'string'))
      ) {
        errors.push(`${label}: ${field} must be a list of strings`);
      }
    }
    if (exp.device !== undefined) {
      const devices = Array.isArray(exp.device) ? exp.device : [exp.device];
      if (!devices.every((d) => DEVICES.includes(d))) {
        errors.push(`${label}: device must be ${DEVICES.join(', ')}`);
      }
    }
    if (exp.group !== undefined && typeof exp.group !== 'string') {
      errors.push(`${label}: group must be a string`);
    }
  });

  return errors;
}
